import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  IconButton,
  Input,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import {
  CheckboxSelect,
  DefaultNumberInputField,
  DefaultScreenTileBox,
  InputField,
  LabeledText,
  useToast,
} from '~/common/components/defaultUIComponents';
import {
  Field,
  FieldArray,
  FieldProps,
  Form,
  FormikContext,
  useField,
  useFormik,
  useFormikContext,
} from 'formik';
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteItem,
  AutoCompleteList,
} from '@choc-ui/chakra-autocomplete';
import { useCreateOrders } from '~/common/api/orderingServiceApi/OrderingApi';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import { unstable_usePrompt as usePrompt } from 'react-router-dom';
import { CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import useTranslate from '~/common/utils/translate';
import { OrderRequest } from '@its4plan/ordering';
import { useMemo, useRef, useState } from 'react';
import * as dateFns from 'date-fns';
import * as yup from 'yup';
import _ from 'lodash';

const createOrderSchema = yup.object({
  customerName: yup.string().notRequired(),
  dueDate: yup.date().notRequired(),
  orders: yup
    .array(
      yup.object({
        customerName: yup.string().required(),
        dueDate: yup.date().required(),
        projects: yup
          .array(
            yup.object({
              amount: yup
                .number()
                .required()
                .min(1, 'createorder.project.amount.warning'),
              options: yup.array(yup.string().min(1).required()).required(),
              comment: yup.string().optional(),
              projectId: yup.number().required(),
              priority: yup.number().min(1).max(10).required(),
            }),
          )
          .required()
          .min(1),
      }),
    )
    .required()
    .min(1),
});

type TCreateOrderSchema = yup.InferType<typeof createOrderSchema>;

export function CreateOrdersV3() {
  const t = useTranslate();
  const toast = useToast({});
  const { mutateAsync: createOrders, isLoading } = useCreateOrders();
  const form = useFormik<TCreateOrderSchema>({
    initialValues: {
      customerName: '',
      dueDate: dateFns.addWeeks(new Date(), 1),
      orders: [],
    },
    validateOnChange: true,
    validationSchema: createOrderSchema,
    onSubmit: async (values, formikHelpers) => {
      await createOrders(mapCreateOrderSchemaToRequest(values));
      formikHelpers.resetForm();
      toast({
        title: 'createorder.success',
        status: 'success',
      });
    },
  });
  const addingDisabled = useMemo(() => {
    const values = form.values;
    if (!values.customerName) {
      return true;
    }
    const enteredCustomerExists = !!values?.orders?.find(
      (order) =>
        order?.customerName === values.customerName &&
        order?.dueDate &&
        values.dueDate &&
        dateFns.isSameDay(order.dueDate, values.dueDate),
    );
    return enteredCustomerExists || form.isSubmitting;
  }, [form.values, form.isSubmitting]);

  usePrompt({
    when: form.dirty && !isLoading,
    message: t('createorder.leave.warning')?.toString() ?? '',
  });

  return (
    <FormikContext.Provider value={form}>
      <Form style={{ height: '100%' }}>
        <Grid
          w="full"
          h="full"
          overflow="hidden"
          templateColumns="repeat(3, 1fr)"
        >
          <GridItem
            colSpan={2}
            display="flex"
            flexDirection="column"
            minHeight="60vh"
            pr={5}
          >
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
              }}
            >
              <Heading size="lg">{t('newOrders')}</Heading>
              <FieldArray
                name="orders"
                render={({ remove, form: { isSubmitting } }) => (
                  <>
                    {form.values.orders.map((order, index) => (
                      <OrderSummary
                        key={`${order.customerName}__${order.dueDate}__${index}`}
                        order={order}
                        remove={remove}
                        index={index}
                        isSubmitting={isSubmitting}
                      />
                    ))}
                  </>
                )}
              />
            </div>
            <Button
              type="submit"
              isLoading={form.isSubmitting}
              isDisabled={form.isSubmitting || !form.isValid}
            >
              {t('createOrder.submit')}
            </Button>
          </GridItem>
          <GridItem colSpan={1}>
            <Tabs h="full" boxShadow="xl" p={5} rounded="md">
              <TabList>
                <Tab>{t('createorder.tabs.order')}</Tab>
              </TabList>
              <TabPanels h="full">
                <TabPanel h="full" p={0} pt={5} pb={35}>
                  <Flex flexDirection="column" h="full" justify="space-between">
                    <Flex flexDirection="column" gap={2}>
                      <InputField
                        name="customerName"
                        label="createorder.tabs.order.client_name"
                        placeholder="createorder.clientName.default"
                      />

                      <Field
                        name="dueDate"
                        render={({
                          field,
                        }: FieldProps<
                          Date | undefined,
                          TCreateOrderSchema
                        >) => (
                          <FormControl>
                            <FormLabel>
                              {t('createOrder.dueDateKW.label')}
                            </FormLabel>
                            <Input
                              type="date"
                              {...field}
                              value={
                                field.value
                                  ? dateFns.format(field.value, 'yyyy-MM-dd')
                                  : undefined
                              }
                              onChange={(e) => {
                                void form.setFieldValue(
                                  'dueDate',
                                  dateFns.parse(
                                    e.target.value,
                                    'yyyy-MM-dd',
                                    new Date(),
                                  ),
                                );
                              }}
                            />
                          </FormControl>
                        )}
                      />
                    </Flex>
                    <Button
                      isDisabled={addingDisabled}
                      onClick={() => {
                        void form.setFieldValue('orders', [
                          ...form.values.orders,
                          {
                            customerName: form.values.customerName,
                            dueDate: form.values.dueDate,
                            projects: [],
                          },
                        ]);
                      }}
                      w="full"
                      mt={5}
                    >
                      {t('createOrder.addOrder')}
                    </Button>
                  </Flex>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </GridItem>
        </Grid>
      </Form>
    </FormikContext.Provider>
  );
}

function OrderSummary(props: {
  order: TCreateOrderSchema['orders'][number];
  remove: (index: number) => void;
  index: number;
  isSubmitting: boolean;
}) {
  return (
    <DefaultScreenTileBox
      boxShadow="md"
      p={2}
      borderRadius="md"
      borderColor={'gray.200'}
      borderWidth={1}
      mb={3}
      position="relative"
    >
      <IconButton
        id={`delete-order-${props.order.customerName}`}
        icon={<CloseIcon />}
        aria-label="delete"
        position="absolute"
        top={3}
        right={3}
        bg="transparent"
        color="red.700"
        isRound
        zIndex={20}
        _hover={{ bg: 'gray.200' }}
        disabled={props.isSubmitting}
        onClick={() => props.remove(props.index)}
      />
      <Grid templateColumns="repeat(2, 1fr)">
        <GridItem>
          <LabeledText fontSize="2xl" fontWeight="semibold">
            {props.order.customerName}
          </LabeledText>
        </GridItem>
        <GridItem>
          {props.order.dueDate && (
            <LabeledText label="createOrder.dueDate.label">
              {props.order.dueDate.toLocaleDateString()}
            </LabeledText>
          )}
        </GridItem>
        <GridItem colSpan={2}>
          <FieldArray
            name={`orders.${props.index}.projects`}
            render={({ remove, push }) => (
              <>
                {props.order.projects.map((project, projectIndex) => (
                  <ProjectOrder
                    key={`project-${project.projectId}`}
                    project={project}
                    remove={remove}
                    orderIndex={props.index}
                    projectIndex={projectIndex}
                  />
                ))}
                <OptionAutocomplete
                  orderIndex={props.index}
                  addProject={push}
                />
              </>
            )}
          />
        </GridItem>
      </Grid>
    </DefaultScreenTileBox>
  );
}

type FormProject = TCreateOrderSchema['orders'][number]['projects'][number];

function ProjectOrder(props: {
  project: FormProject;
  orderIndex: number;
  projectIndex: number;
  remove: (index: number) => void;
}) {
  const { isSubmitting } = useFormikContext();
  const [{ value: order }] = useField<TCreateOrderSchema['orders'][number]>(
    `orders.${props.orderIndex}`,
  );
  const { data: projects } = ProjectApi.hooks.useProjectsQuery2();
  const [project, options] = useMemo(() => {
    const project = projects?.find(
      (project) => project.id === props.project.projectId,
    );
    const options =
      project?.option_ids
        ?.map((oid) => projects?.find((p) => p.id == oid))
        .filter((option) => !!option) ?? [];
    return [project, options];
  }, [projects]);

  return (
    <Grid
      borderBottomWidth={1}
      borderBottomColor="gray.400"
      pb={3}
      mb={1}
      columnGap={2}
      templateColumns="repeat(4, 1fr)"
      alignItems="start"
    >
      <GridItem colSpan={1} mt={7}>
        <Flex alignItems="center">
          <IconButton
            mb={1}
            icon={<DeleteIcon />}
            isRound
            bg="transparent"
            color="red.700"
            aria-label="delte"
            mt={0.5}
            _hover={{ bg: 'gray.100' }}
            disabled={isSubmitting}
            onClick={() => props.remove(props.projectIndex)}
          />
          <LabeledText fontSize="larger">{project?.name}</LabeledText>
        </Flex>
      </GridItem>
      <GridItem colSpan={1}>
        <DefaultNumberInputField
          name={`orders.${props.orderIndex}.projects.${props.projectIndex}.amount`}
          label="createorder.project.amount"
          min={0}
          isDisabled={isSubmitting}
          labelProps={{ mb: 1 }}
        />
      </GridItem>
      <GridItem colSpan={1}>
        <DefaultNumberInputField
          name={`orders.${props.orderIndex}.projects.${props.projectIndex}.priority`}
          label="createorder.project.weekplan_kw"
          min={1}
          max={10}
          isDisabled={isSubmitting}
          labelProps={{ mb: 1 }}
          width="80%"
        />
      </GridItem>
      <GridItem colSpan={4}>
        <InputField
          textarea
          label="createorder.project.comment"
          name={`orders.${props.orderIndex}.projects.${props.projectIndex}.comment`}
          rows={1}
          isDisabled={isSubmitting}
        />
      </GridItem>
      <GridItem colSpan={4}>
        <CheckboxSelect
          label="createorder.project.options"
          name={`orders.${props.orderIndex}.projects.${props.projectIndex}.options`}
          disabled={isSubmitting}
          columns={2}
          fields={options}
          labels={(option) => (option ? option.name : '')}
          keys={(option) =>
            option
              ? `${option.id}_${order.customerName}_${order.dueDate}_${props.project.projectId}`
              : ''
          }
          values={(option) => (option ? option.id : -1)}
        />
      </GridItem>
    </Grid>
  );
}

function OptionAutocomplete(props: {
  addProject: (project: FormProject) => void;
  orderIndex: number;
}) {
  const { isSubmitting } = useFormikContext();
  const [{ data: projects = [] }] = ProjectApi.hooks.useProjectsQuery();
  const translate = useTranslate();
  const [selectedProject, setSelectedProject] = useState<number>();

  const [{ value: order }] = useField<TCreateOrderSchema['orders'][number]>(
    `orders.${props.orderIndex}`,
  );

  const autoCompleteRef = useRef<HTMLInputElement>(null);
  return (
    <AutoComplete
      rollNavigation
      openOnFocus
      onChange={(projectId: string | undefined) =>
        setSelectedProject(Number(projectId))
      }
      filter={(query: string, _value: string, label: string) =>
        label.includes(query)
      }
      focusInputOnSelect
      suggestWhenEmpty
      listAllValuesOnFocus
      onSelectOption={({ item }) => {
        props.addProject({
          projectId: Number(item.value),
          amount: 0,
          comment: '',
          options: [],
          priority: 1,
        });
        setSelectedProject(undefined);
      }}
      defaultIsOpen
    >
      {({ isOpen }) => {
        return (
          <>
            <Flex w="full">
              <AutoCompleteInput
                variant="filled"
                w="50%"
                autoFocus
                placeholder={translate(
                  'createorder.autocomplete.additional.project',
                )}
                disabled={isSubmitting}
                onFocus={(e) => (e.target.value = '')}
                ref={autoCompleteRef}
                value={
                  isOpen || !selectedProject
                    ? undefined
                    : projects.find((project) => project.id === selectedProject)
                        ?.name || undefined
                }
              />
            </Flex>
            <AutoCompleteList>
              {_.orderBy(projects, ['name'], 'asc')
                .map((project) => ({
                  value: `${project.id}`,
                  label: project.name,
                  isOption: project.is_option,
                }))
                .filter((project) => !project.isOption)
                .map(({ label, value, isOption }) => (
                  <AutoCompleteItem
                    key={`${order.customerName}__${order.dueDate}__${value}__select_itme`}
                    value={value}
                    label={label}
                  >
                    {isOption && translate('createorder.item.option')} {label}
                  </AutoCompleteItem>
                ))}
            </AutoCompleteList>
          </>
        );
      }}
    </AutoComplete>
  );
}

function mapCreateOrderSchemaToRequest(
  values: TCreateOrderSchema,
): OrderRequest[] {
  return values.orders.map((order) => ({
    ...order,
    projects: order.projects.map((project) => ({
      ...project,
      options: project.options.map(Number),
    })),
  }));
}
